// pages/TermsOfService.js
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  FileText, 
  Check, 
  AlertCircle, 
  Shield, 
  Users, 
  Smartphone, 
  DollarSign, 
  Clock, 
  ArrowRight, 
  Mail, 
  Phone,
  ChevronUp,
  Sparkles,
  Heart,
  Scale,
  BookOpen,
  BadgeCheck,
  Handshake,
  Gavel
} from 'lucide-react';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeSection, setActiveSection] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionsRef = useRef({});

  const lastUpdated = "January 15, 2026";

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for active section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    Object.values(sectionsRef.current).forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const tocItems = [
    { id: 'acceptance', label: 'Acceptance of Terms', icon: Handshake },
    { id: 'services', label: 'Our Services', icon: BookOpen },
    { id: 'accounts', label: 'User Accounts', icon: Users },
    { id: 'data', label: 'Your Data & Privacy', icon: Shield },
    { id: 'sms', label: 'SMS & Communications', icon: Smartphone },
    { id: 'fees', label: 'Fees & Payments', icon: DollarSign },
    { id: 'conduct', label: 'User Conduct', icon: BadgeCheck },
    { id: 'intellectual', label: 'Intellectual Property', icon: Gavel },
    { id: 'disclaimer', label: 'Disclaimer of Warranties', icon: AlertCircle },
    { id: 'liability', label: 'Limitation of Liability', icon: Scale },
    { id: 'termination', label: 'Termination', icon: Clock },
    { id: 'contact', label: 'Contact Us', icon: Mail }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      {/* Scroll Progress Bar */}
      <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} />

      {/* Hero Section */}
      <section className="legal-hero-modern">
        <div className="wrap">
          <div className="legal-hero-content">
            <div className="legal-hero-badge">
              <FileText size={16} />
              <span>Terms of Service</span>
            </div>
            <h1 className="legal-hero-title">
              Fair, clear, and <span className="highlight">simple terms.</span>
            </h1>
            <p className="legal-hero-description">
              Understand how FaithConnect works, your rights, and our responsibilities.
            </p>
            <div className="legal-hero-meta">
              <div className="legal-hero-meta-item">
                <Clock size={16} />
                <span>Last updated: {lastUpdated}</span>
              </div>
              <div className="legal-hero-meta-divider" />
              <div className="legal-hero-meta-item">
                <BookOpen size={16} />
                <span>10 min read</span>
              </div>
              <div className="legal-hero-meta-divider" />
              <div className="legal-hero-meta-item">
                <Shield size={16} />
                <span>Fair & Transparent</span>
              </div>
            </div>
          </div>
        </div>
        <div className="legal-hero-particles">
          {[...Array(6)].map((_, i) => (
            <div 
              key={i} 
              className="legal-hero-particle"
              style={{
                '--delay': `${i * 0.3}s`,
                '--x': `${Math.random() * 100}%`,
                '--y': `${Math.random() * 100}%`
              }}
            />
          ))}
        </div>
      </section>

      {/* Layout */}
      <section className="legal-layout">
        <div className="wrap">
          <div className="legal-layout-grid">
            {/* Sidebar TOC */}
            <aside className="legal-sidebar">
              <div className="legal-toc-sticky">
                <div className="legal-toc-modern">
                  <div className="legal-toc-header-modern">
                    <FileText size={20} />
                    <h3>Contents</h3>
                  </div>
                  <nav className="legal-toc-nav">
                    {tocItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className={`legal-toc-nav-item ${isActive ? 'active' : ''}`}
                        >
                          <Icon size={16} />
                          <span>{item.label}</span>
                          {isActive && <div className="legal-toc-nav-indicator" />}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="legal-main">
              {/* Acceptance of Terms */}
              <div 
                id="acceptance" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['acceptance'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">01</div>
                  <h2>Acceptance of Terms</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>
                    By using FaithConnect ("we," "our," or "us"), you agree to these Terms of Service 
                    ("Terms"). If you don't agree, please don't use our Services.
                  </p>
                  <p>
                    These Terms apply to all users, including church administrators, members, 
                    and visitors. By using our Services, you confirm that you have the authority 
                    to bind your church or organization to these Terms.
                  </p>
                  <div className="legal-highlight-modern">
                    <div className="legal-highlight-icon">
                      <Handshake size={20} />
                    </div>
                    <div>
                      <h4>Simple Agreement</h4>
                      <p>Using FaithConnect means you accept these terms. It's that straightforward.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Our Services */}
              <div 
                id="services" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['services'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">02</div>
                  <h2>Our Services</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>FaithConnect provides church member care software including:</p>
                  
                  <div className="legal-services-grid">
                    {[
                      { icon: '📊', title: 'Attendance Tracking', desc: 'Tools to record and monitor church attendance.' },
                      { icon: '👥', title: 'Member Management', desc: 'Store and organize member information.' },
                      { icon: '📱', title: 'SMS Communication', desc: 'Send bulk SMS messages to members.' },
                      { icon: '📈', title: 'Analytics', desc: 'Generate reports and insights about your church.' },
                      { icon: '⚡', title: 'Automation', desc: 'Automated follow-ups and notifications.' }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-service-item">
                        <div className="legal-service-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="legal-note-text">
                    We reserve the right to modify or discontinue any feature at any time, 
                    with reasonable notice to users.
                  </p>
                </div>
              </div>

              {/* User Accounts */}
              <div 
                id="accounts" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['accounts'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">03</div>
                  <h2>User Accounts</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>To use our Services, you must:</p>
                  
                  <div className="legal-requirements-grid">
                    {[
                      { icon: '👤', title: 'Be 18+', desc: 'You must be at least 18 years old.' },
                      { icon: '✅', title: 'Accurate Information', desc: 'Keep your account information current.' },
                      { icon: '🔒', title: 'Protect Your Credentials', desc: "You're responsible for all activity under your account." },
                      { icon: '🚨', title: 'Notify Us', desc: 'Inform us immediately of any unauthorized access.' }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-requirement-item">
                        <div className="legal-requirement-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="legal-highlight-modern gold">
                    <div className="legal-highlight-icon">
                      <AlertCircle size={20} />
                    </div>
                    <div>
                      <h4>Account Security</h4>
                      <p>You're responsible for your account. Keep your password secure and don't share it.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Data & Privacy */}
              <div 
                id="data" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['data'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">04</div>
                  <h2>Your Data & Privacy</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>Your data belongs to you. We process it only as necessary to provide our Services.</p>
                  
                  <div className="legal-data-privacy-grid">
                    {[
                      { icon: '👑', title: 'Data Ownership', desc: 'You retain all rights to your church member data.' },
                      { icon: '⚙️', title: 'Data Processing', desc: 'We process data on your behalf as a data processor.' },
                      { icon: '📤', title: 'Data Export', desc: 'You can export your data at any time.' },
                      { icon: '🗑️', title: 'Data Deletion', desc: 'We delete your data when you terminate your account.' }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-data-privacy-item">
                        <div className="legal-data-privacy-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="legal-privacy-prompt">
                    <Shield size={20} />
                    <div>
                      <p>For more details, see our <Link to="/privacy" className="legal-link">Privacy Policy</Link>.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* SMS & Communications */}
              <div 
                id="sms" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['sms'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">05</div>
                  <h2>SMS & Communications</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>Our SMS features are subject to these conditions:</p>
                  
                  <div className="legal-sms-grid">
                    {[
                      { icon: '📝', title: 'Consent', desc: 'You must have proper consent from members to contact them via SMS.' },
                      { icon: '📄', title: 'Content', desc: "You're responsible for all SMS content you send." },
                      { icon: '🚫', title: 'Opt-Out', desc: 'Members can opt out of SMS communications at any time.' },
                      { icon: '⚖️', title: 'Compliance', desc: 'You must comply with all applicable telecommunications laws.' }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-sms-item">
                        <div className="legal-sms-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="legal-highlight-modern blue">
                    <div className="legal-highlight-icon">
                      <Smartphone size={20} />
                    </div>
                    <div>
                      <h4>Respect Member Privacy</h4>
                      <p>Only send SMS to members who have given consent. It's the right thing to do.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fees & Payments */}
              <div 
                id="fees" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['fees'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">06</div>
                  <h2>Fees & Payments</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>Our pricing is transparent and straightforward:</p>
                  
                  <div className="legal-fees-grid">
                    {[
                      { icon: '💰', title: 'Subscription Fees', desc: 'Paid monthly or annually as specified.' },
                      { icon: '💳', title: 'Payment Methods', desc: 'We accept Mobile Money, bank transfers, and cards.' },
                      { icon: '↩️', title: 'Refunds', desc: 'We offer prorated refunds for unused subscription time.' },
                      { icon: '📢', title: 'Price Changes', desc: "We'll notify you at least 30 days before any price increase." }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-fees-item">
                        <div className="legal-fees-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="legal-note-modern">
                    <div className="legal-note-icon">💵</div>
                    <div>
                      <strong>Currency:</strong> All fees are in Ghana Cedis (GHS) unless otherwise stated.
                    </div>
                  </div>

                  <div className="legal-highlight-modern gold">
                    <div className="legal-highlight-icon">
                      <DollarSign size={20} />
                    </div>
                    <div>
                      <h4>No Hidden Fees</h4>
                      <p>No hidden fees. No surprise charges. Just clear, fair pricing.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Conduct */}
              <div 
                id="conduct" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['conduct'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">07</div>
                  <h2>User Conduct</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>You agree to use our Services responsibly:</p>
                  
                  <div className="legal-conduct-grid">
                    {[
                      { icon: '🚫', title: 'No Abuse', desc: "Don't use our Services for illegal or harmful purposes." },
                      { icon: '📧', title: 'No Spam', desc: "Don't send unsolicited or spam messages." },
                      { icon: '👤', title: 'No Impersonation', desc: "Don't impersonate others." },
                      { icon: '🔓', title: 'No Interference', desc: "Don't disrupt our Services or attempt unauthorized access." },
                      { icon: '📊', title: 'No Data Scraping', desc: "Don't scrape or extract data without permission." }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-conduct-item">
                        <div className="legal-conduct-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="legal-note-text">
                    We reserve the right to suspend or terminate accounts that violate these conduct rules.
                  </p>
                </div>
              </div>

              {/* Intellectual Property */}
              <div 
                id="intellectual" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['intellectual'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">08</div>
                  <h2>Intellectual Property</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>FaithConnect's software, design, logos, and content are protected by intellectual property laws.</p>
                  
                  <div className="legal-ip-grid">
                    {[
                      { icon: '©️', title: 'Our Rights', desc: 'We own all rights to the FaithConnect platform and brand.' },
                      { icon: '📁', title: 'Your Rights', desc: 'You own all rights to your church data and content.' },
                      { icon: '📋', title: 'License', desc: 'We grant you a non-exclusive, non-transferable license to use our Services.' },
                      { icon: '💡', title: 'Feedback', desc: 'Any feedback you provide may be used to improve our Services.' }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-ip-item">
                        <div className="legal-ip-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="legal-highlight-modern subtle">
                    <div className="legal-highlight-icon">
                      <Gavel size={20} />
                    </div>
                    <div>
                      <h4>Respect Our Work</h4>
                      <p>You may not copy, modify, or reverse-engineer our software.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Disclaimer of Warranties */}
              <div 
                id="disclaimer" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['disclaimer'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">09</div>
                  <h2>Disclaimer of Warranties</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>Our Services are provided "as is" and "as available" without warranties of any kind.</p>
                  
                  <div className="legal-disclaimer-grid">
                    {[
                      { icon: '⚠️', title: 'No Guarantees', desc: "We don't guarantee uninterrupted or error-free service." },
                      { icon: '📶', title: 'Third-Party Dependencies', desc: 'Our Services rely on telecommunications networks we control.' },
                      { icon: '🎯', title: 'Results', desc: "We don't guarantee specific results from using our Services." }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-disclaimer-item">
                        <div className="legal-disclaimer-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="legal-note-modern">
                    <div className="legal-note-icon">💪</div>
                    <div>
                      <strong>Our Commitment:</strong> We're committed to reliability, but technology is never perfect. 
                      We'll do our best to keep things running smoothly.
                    </div>
                  </div>
                </div>
              </div>

              {/* Limitation of Liability */}
              <div 
                id="liability" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['liability'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">10</div>
                  <h2>Limitation of Liability</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>To the maximum extent permitted by law, FaithConnect is not liable for:</p>
                  
                  <div className="legal-liability-grid">
                    {[
                      { icon: '💔', title: 'Indirect Damages', desc: 'Lost profits, data loss, or business interruption.' },
                      { icon: '🏢', title: 'Third-Party Actions', desc: 'Actions or omissions of third parties (e.g., telecom providers).' },
                      { icon: '📝', title: 'User Content', desc: 'Content you create or share through our Services.' }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-liability-item">
                        <div className="legal-liability-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="legal-note-text">
                    Our total liability is limited to the amount you paid us in the previous 12 months.
                  </p>

                  <div className="legal-highlight-modern blue">
                    <div className="legal-highlight-icon">
                      <Shield size={20} />
                    </div>
                    <div>
                      <h4>Fair and Reasonable</h4>
                      <p>We'll always do right by you. These terms are designed to be fair to everyone.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Termination */}
              <div 
                id="termination" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['termination'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">11</div>
                  <h2>Termination</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>Either party may terminate the relationship:</p>
                  
                  <div className="legal-termination-grid">
                    {[
                      { icon: '👤', title: 'By You', desc: 'You can cancel your account at any time through your account settings.' },
                      { icon: '🏢', title: 'By Us', desc: 'We may terminate or suspend your account for violation of these Terms.' },
                      { icon: '📤', title: 'Data Retrieval', desc: 'You have 30 days after termination to export your data.' }
                    ].map((item, idx) => (
                      <div key={idx} className="legal-termination-item">
                        <div className="legal-termination-icon">{item.icon}</div>
                        <div>
                          <h5>{item.title}</h5>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="legal-note-text">
                    Upon termination, we'll delete your data in accordance with our data retention policies.
                  </p>
                </div>
              </div>

              {/* Contact */}
              <div 
                id="contact" 
                className="legal-section-modern"
                ref={el => sectionsRef.current['contact'] = el}
              >
                <div className="legal-section-header-modern">
                  <div className="legal-section-number-modern">12</div>
                  <h2>Contact Us</h2>
                </div>
                <div className="legal-section-body-modern">
                  <p>Questions about these Terms? Reach out to us:</p>
                  
                  <div className="legal-contact-grid-modern">
                    <a href="mailto:legal@faithconnect.com" className="legal-contact-item-modern">
                      <div className="legal-contact-icon">
                        <Mail size={20} />
                      </div>
                      <div className="legal-contact-text">
                        <span className="legal-contact-label">Email</span>
                        <span className="legal-contact-value">legal@faithconnect.com</span>
                      </div>
                      <ArrowRight size={16} className="legal-contact-arrow" />
                    </a>
                    
                    <a href="tel:+233200000000" className="legal-contact-item-modern">
                      <div className="legal-contact-icon">
                        <Phone size={20} />
                      </div>
                      <div className="legal-contact-text">
                        <span className="legal-contact-label">Phone</span>
                        <span className="legal-contact-value">+233 20 000 0000</span>
                      </div>
                      <ArrowRight size={16} className="legal-contact-arrow" />
                    </a>
                    
                    <div className="legal-contact-item-modern">
                      <div className="legal-contact-icon">
                        <MapPin size={20} />
                      </div>
                      <div className="legal-contact-text">
                        <span className="legal-contact-label">Location</span>
                        <span className="legal-contact-value">Accra, Ghana</span>
                      </div>
                    </div>
                  </div>

                  <div className="legal-response-time">
                    <div className="legal-response-time-icon">⏰</div>
                    <div>
                      <h4>We're Here to Help</h4>
                      <p>Usually respond within 48 hours. Your questions and concerns are important to us.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Back to Top */}
              <div className="legal-back-to-top">
                <button onClick={() => scrollToSection('acceptance')} className="legal-back-to-top-btn">
                  <ChevronUp size={20} />
                  Back to top
                </button>
              </div>
            </main>
          </div>
        </div>
      </section>
    </>
  );
};

export default TermsOfService;